<template>
  <VMenu
    transition="slide-y-transition"
    bottom
    :aria-label="translate('aria_label_profile_nav')"
    location="bottom center"
    :open-on-hover="$vuetify.display.mdAndUp"
    open-delay="100"
  >
    <template v-slot:activator="{ props }">
      <button
        type="button"
        v-bind="props"
        id="profile-nav"
        class="font-size-m d-flex flex-column align-center justify-center h-100 px-2 px-md-0"
      >
        <SvgWrapper use="icon_svg_user" dimensions="30" alt="Profil" />
        <a
          :href="showsAccount ? pageUrl.PROFILE || '/' : loginUrl"
          @click="handleAccountLinkClick"
          class="d-none d-md-flex align-center py-1"
        >
          <span
            v-if="showsAccount"
            class="text-uppercase font-size-s text-no-wrap"
          >
            {{ translate('header_navigation_my_account') }}
          </span>
          <span v-else class="text-uppercase font-size-s">{{
            translate('header_navigation_login')
          }}</span>
          <SvgWrapper
            class="ml-1 d-md-none d-lg-inline-block"
            use="icon_svg_down"
            dimensions="17"
            alt="Down"
          />
        </a>
      </button>
    </template>
    <VList v-if="showsAccount">
      <VListItem :href="pageUrl.PROFILE">
        <VListItemTitle>{{
          translate('header_navigation_profile')
        }}</VListItemTitle>
      </VListItem>
      <VListItem :href="pageUrl.ORDERS">
        <VListItemTitle>{{
          translate('header_navigation_orders')
        }}</VListItemTitle>
      </VListItem>
      <VListItem :href="pageUrl.REVIEWS">
        <VListItemTitle>{{
          translate('header_navigation_reviews')
        }}</VListItemTitle>
      </VListItem>
      <VDivider opacity=".75" class="my-2" />
      <VListItem :href="logoutUrl" @click.prevent="goToLogout">
        <VListItemTitle>{{
          translate('header_navigation_logout')
        }}</VListItemTitle>
      </VListItem>
    </VList>
    <VList v-else>
      <VListItem :to="loginUrl" @click.prevent="goToLogin">
        <VListItemTitle>{{
          translate('header_navigation_login')
        }}</VListItemTitle>
      </VListItem>
      <VListItem :to="registerUrl" @click.prevent="goToRegister">
        <VListItemTitle>{{
          translate('header_navigation_register')
        }}</VListItemTitle>
      </VListItem>
    </VList>
  </VMenu>
</template>

<script>
import { useCustomerStore } from '@/stores/customer'
import { mapActions, mapState } from 'pinia'
import SvgWrapper from './SvgWrapper.vue'

export default {
  components: { SvgWrapper },
  computed: {
    ...mapState(useCustomerStore, ['customerSoftLoggedIn']),
    /**
     * The session state, but never before this component has mounted.
     *
     * The server renders this header anonymously — it has to, since the page
     * is shared by every visitor through the CDN cache. establishSession()
     * fills the customer store a few ticks into the client's life, and this
     * subtree hydrates behind an async boundary, so its first render could
     * already see a logged-in store and disagree with the server markup: a
     * hydration mismatch on every logged-in visit to a server-rendered page.
     * Rendering the logged-out branch first and swapping after mount matches
     * the server whatever the hydration timing turns out to be — the same
     * shape as the DPR pin in ProductImage (VIP-12333).
     */
    showsAccount() {
      return this.hasMounted && this.customerSoftLoggedIn
    },
    // $route.path instead of location.pathname: same value in the browser,
    // but it also exists during a server render (VIP-12037)
    loginUrl() {
      return `/auth/v2/login?redirect_uri=${this.$route.path}`
    },
    logoutUrl() {
      return this.pageUrl.LOGOUT + `?redirect_uri=${this.$route.path}`
    },
    registerUrl() {
      return `/auth/v2/register?redirect_uri=${this.$route.path}`
    }
  },
  data() {
    return {
      pageUrl: {},
      hasMounted: false
    }
  },
  methods: {
    ...mapActions(useCustomerStore, { logoutCustomer: 'logout' }),
    handleAccountLinkClick(event) {
      if (this.customerSoftLoggedIn) return

      event.preventDefault()
      return this.goToLogin()
    },
    goToLogout() {
      // The logout URL logs out server-side, so this request's outcome doesn't matter
      this.logoutCustomer().catch(() => {})
      this.clearCacheAndNavigate(this.logoutUrl)
    },
    async goToLogin() {
      await this.clearCache()
      this.$router.push(this.loginUrl)
    },
    async goToRegister() {
      await this.clearCache()
      this.$router.push(this.registerUrl)
    },
    async clearCache() {
      const [{ default: CacheHelper }, { default: BulkProductHelper }] =
        await Promise.all([
          import('@/services/helpers/CacheHelper'),
          import('@/modules/product/BulkProductHelper')
        ])
      await Promise.all([
        await BulkProductHelper.eraseAllCache(),
        await CacheHelper.deleteCache('gatekeeper')
      ])
    },
    async clearCacheAndNavigate(href) {
      await this.clearCache()
      window.location.href = href
    }
  },
  async created() {
    const { PAGE_URL } = await import('@/constants/GlobalConstants')
    this.pageUrl = PAGE_URL
  },
  mounted() {
    // Flips showsAccount on; see the comment there.
    this.hasMounted = true
  }
}
</script>
